/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/
// React components
import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import partCodesService from "services/part-code-service";
import formatPartCode from "helper/formatPartCode";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Icon from "@mui/material/Icon";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// Material Dashboard React components;
import MDBox from "components/MDBox";
import breakpoints from "assets/theme/base/breakpoints";

// E4A Components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";

// Specific view Tab components
import DetailsTab from "./details-tab";
import BOMTab from "./bom-tab";
import AttachmentsTab from "./attachments-tab";
import HistoryTab from "./history-tab";

function PartSpecificView() {
  const location = useLocation();
  const approveMode = location.state?.approveMode || false;
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [partCode, setPartCode] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const { id } = useParams();
  const [title, setTitle] = useState("Part Specific View");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // Fetch data
        const response = await partCodesService.getPartById(id);
        setTitle(
          formatPartCode(response.data.attributes).replace(/-/g, '–')
        )
        setPartCode(response.data.attributes);
      } catch (error) {
        console.error("Error fetching part code data:", error);
        // Navigate back to the part codes list if there's an error
        navigate("/part-code-management/part-code-view");
      }
    })();
  }, []);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);


  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={title} />
      <MDBox mt={2}>
        <AppBar position="static">
          {/* Tabs navigation bar */}
          <Tabs orientation={tabsOrientation} value={tabValue} onChange={handleSetTabValue}>
            <Tab
              label="Details"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  segment
                </Icon>
              }
            />
            <Tab
              label="BOM"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  description
                </Icon>
              }
            />
            <Tab
              label="Attachments"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  attachment
                </Icon>
              }
            />
            <Tab
              label="History"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  history
                </Icon>
              }
            />
          </Tabs>
        </AppBar>
      </MDBox>

      {/* Tab content */}
      {tabValue === 0 && (<DetailsTab approveMode={approveMode} />)}
      {tabValue === 1 && (<BOMTab />)}
      {tabValue === 2 && (<AttachmentsTab />)}
      {tabValue === 3 && (<HistoryTab partCode={partCode} />)}

      <Footer />
    </DashboardLayout>
  );
}

export default PartSpecificView;
